import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@lib/msal';
import { useAllModuleAccess } from '@utils/moduleAccess';
import { useAuthStore } from '@store/authStore';
import { accessibleApps, APP_ROLE_LABELS } from '@lib/linkedApps';
import {
  Users,
  Building2,
  UserCheck,
  CalendarDays,
  TrendingUp,
  ArrowRight,
  AlertCircle,
  Loader2,
  RefreshCw,
  CheckSquare,
  ExternalLink,
  CreditCard,
  CircleDashed,
  Clock,
  AppWindow,
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface BeaconStats {
  peopleCount: number;
  organisationsCount: number;
  membersCount: number;
  lastUpdated: string;
  cachedAt?: string;
  fromCache?: boolean;
  warning?: string;
  error?: string;
}

interface BeaconTaskCounts {
  userOpenTasks: number;
  unassignedOpenTasks: number;
  userPersonId: number | null;
  accountId: string | null;
  cachedAt?: string;
  fromCache?: boolean;
}

interface CardAttributionsResult {
  attributions: CardAttribution[];
  cachedAt?: string;
}

interface CardAttribution {
  memberName: string;
  memberEmail: string;
  initials: string;
  debitCardIdentifier: string;
  transactions: { id: string; date: string; description: string; amount: number; unexplainedAmount: number; status?: 'unexplained' | 'unapproved' }[];
  totalUnexplained: number;
  unapprovedCount?: number;
}

async function fetchCardAttributions(): Promise<CardAttributionsResult> {
  const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
  const token = await getAccessToken();
  const response = await fetch(`${apiBase}/api/freeagent/overview`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return { attributions: [] };
  const data = await response.json();
  return {
    attributions: (data.data?.cardAttributions as CardAttribution[]) || [],
    cachedAt: data.data?.cachedAt as string | undefined,
  };
}

async function fetchBeaconStats(refresh = false): Promise<BeaconStats> {
  const apiUrl = import.meta.env.VITE_API_URL || '/api';
  const url = refresh ? `${apiUrl}/beacon/stats?refresh=true` : `${apiUrl}/beacon/stats`;
  const token = await getAccessToken();
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error('Failed to fetch stats');
  }
  return response.json();
}

async function fetchBeaconTaskCounts(): Promise<BeaconTaskCounts> {
  const apiUrl = import.meta.env.VITE_API_URL || '/api';
  const token = await getAccessToken();
  const response = await fetch(`${apiUrl}/beacon/tasks/counts`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error('Failed to fetch task counts');
  }
  const result = await response.json();
  return result.data;
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  lastRetrieved?: string;
  loading?: boolean;
  icon: React.ElementType;
  warning?: boolean;
}

function StatCard({ title, value, subtitle, lastRetrieved, loading, icon: Icon, warning }: StatCardProps) {
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {loading ? (
            <div className="mt-2 flex items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
              {subtitle && (
                <p className={`mt-2 text-sm ${warning ? 'text-amber-600' : 'text-gray-500'}`}>
                  {warning && <AlertCircle className="inline h-3 w-3 mr-1" />}
                  {subtitle}
                </p>
              )}
              {lastRetrieved && (
                <p className="mt-1 text-xs text-gray-400">Updated {lastRetrieved}</p>
              )}
            </>
          )}
        </div>
        <div className="p-3 bg-sca-purple-50 rounded-lg">
          <Icon className="h-6 w-6 text-sca-purple-500" />
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(dateString?: string): string {
  if (!dateString) return '';
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 2)   return 'just now';
  if (diffMins < 60)  return `${diffMins} mins ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7)   return `${diffDays} days ago`;
  if (diffDays < 14)  return '1 week ago';
  return 'more than a week ago';
}

export default function Dashboard() {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();
  const userName = accounts[0]?.name?.split(' ')[0] || 'there';
  const moduleAccess = useAllModuleAccess();
  const { appAccess } = useAuthStore();
  const myApps = accessibleApps(appAccess);

  // Fetch Beacon stats
  const { data: beaconStats, isLoading: statsLoading } = useQuery({
    queryKey: ['beacon-stats'],
    queryFn: () => fetchBeaconStats(false),
    staleTime: 5 * 60 * 1000, // Consider stale after 5 minutes
    refetchInterval: 5 * 60 * 1000, // Check every 5 minutes
  });

  // Fetch Beacon task counts
  const { data: taskCounts, isLoading: tasksLoading } = useQuery({
    queryKey: ['beacon-task-counts'],
    queryFn: () => fetchBeaconTaskCounts(),
    staleTime: 2 * 60 * 1000, // Consider stale after 2 minutes
    refetchInterval: 2 * 60 * 1000, // Check every 2 minutes
  });

  // Fetch card attributions from FreeAgent
  const { data: cardAttributionsResult, isLoading: cardAttributionsLoading } = useQuery({
    queryKey: ['card-attributions'],
    queryFn: fetchCardAttributions,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
  const cardAttributions = cardAttributionsResult?.attributions;
  const cardAttributionsCachedAt = cardAttributionsResult?.cachedAt;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Force refresh from Beacon API (bypass server cache)
      const freshData = await fetchBeaconStats(true);
      queryClient.setQueryData(['beacon-stats'], freshData);
      // Invalidate other queries so they refetch on next render
      await queryClient.invalidateQueries({ queryKey: ['beacon-task-counts'] });
      await queryClient.invalidateQueries({ queryKey: ['card-attributions'] });
    } finally {
      setIsRefreshing(false);
    }
  };

  const hasWarning = beaconStats?.warning || beaconStats?.error;
  const beaconLastRetrieved = formatTimeAgo(beaconStats?.cachedAt);
  const tasksLastRetrieved = formatTimeAgo(taskCounts?.cachedAt);
  const financeLastRetrieved = formatTimeAgo(cardAttributionsCachedAt);

  return (
    <div className="space-y-6">
      {/* Welcome message */}
      <div className="card bg-gradient-to-r from-sca-purple-500 to-sca-pink-500 text-white">
        <h2 className="text-xl font-semibold">Welcome back, {userName}!</h2>
        <p className="mt-1 text-white/80">
          Here&apos;s what&apos;s happening with SCA UK today.
        </p>
      </div>

      {/* Warning banner if Beacon is not connected */}
      {hasWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Beacon CRM Connection Issue</p>
            <p className="text-sm text-amber-700 mt-1">
              {beaconStats?.warning || beaconStats?.error}
            </p>
          </div>
        </div>
      )}

      {/* Linked apps this user can access */}
      {myApps.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Apps</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myApps.map((linkedApp) => {
              const role = appAccess?.[linkedApp.id];
              const tile = (
                <div className="card hover:shadow-md transition-shadow h-full">
                  <div className="flex items-start justify-between">
                    <div className="p-3 bg-sca-purple-50 rounded-lg">
                      <AppWindow className="h-6 w-6 text-sca-purple-500" />
                    </div>
                    {role && (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {APP_ROLE_LABELS[role]}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-semibold text-gray-900 flex items-center gap-1.5">
                    {linkedApp.name}
                    {linkedApp.url && <ExternalLink className="h-3.5 w-3.5 text-gray-400" />}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">{linkedApp.description}</p>
                  {!linkedApp.url && (
                    <p className="mt-2 text-xs text-amber-600">Launch URL not configured</p>
                  )}
                </div>
              );
              return linkedApp.url ? (
                <a
                  key={linkedApp.id}
                  href={linkedApp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  {tile}
                </a>
              ) : (
                <div key={linkedApp.id}>{tile}</div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats header with refresh */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Beacon CRM Overview</h3>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh all dashboard data"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats grid — cards hidden when the user has no access to that module */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {moduleAccess.people !== 'none' && (
          <StatCard
            title="People"
            value={beaconStats?.peopleCount ?? 0}
            loading={statsLoading}
            icon={Users}
            lastRetrieved={beaconLastRetrieved}
          />
        )}
        {moduleAccess.organisations !== 'none' && (
          <StatCard
            title="Organisations"
            value={beaconStats?.organisationsCount ?? 0}
            loading={statsLoading}
            icon={Building2}
            lastRetrieved={beaconLastRetrieved}
          />
        )}
        {moduleAccess.volunteers !== 'none' && (
          <StatCard
            title="Members"
            value={beaconStats?.membersCount ?? 0}
            subtitle={beaconStats?.membersCount === 0 ? 'Launching soon' : 'Active memberships'}
            loading={statsLoading}
            icon={UserCheck}
            warning={beaconStats?.membersCount === 0}
            lastRetrieved={beaconLastRetrieved}
          />
        )}
        <StatCard
          title="Upcoming Events"
          value="—"
          subtitle="Coming soon"
          icon={CalendarDays}
        />
      </div>

      {/* Beacon Tasks card */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-sca-purple-500" />
            Beacon Tasks
          </h3>
          {taskCounts?.accountId && (
            <a
              href={`https://app.beaconcrm.org/account/${taskCounts.accountId}/records/task/list`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-sca-purple-500 hover:text-sca-purple-600 font-medium flex items-center gap-1"
            >
              View all in Beacon
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        {tasksLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* My Open Tasks */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">My Open Tasks</p>
              {taskCounts?.accountId ? (
                <a
                  href={`https://app.beaconcrm.org/account/${taskCounts.accountId}/records/task/list`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2"
                >
                  <span className="text-3xl font-semibold text-gray-900 group-hover:text-sca-purple-600 transition-colors">
                    {taskCounts?.userOpenTasks ?? 0}
                  </span>
                  <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-sca-purple-500 transition-colors" />
                </a>
              ) : (
                <p className="text-3xl font-semibold text-gray-900">
                  {taskCounts?.userOpenTasks ?? 0}
                </p>
              )}
              {taskCounts?.userOpenTasks === 0 && (
                <p className="text-xs text-gray-400 mt-1">No tasks assigned to you</p>
              )}
            </div>

            {/* Unassigned Tasks */}
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Unassigned Tasks</p>
              {taskCounts?.accountId ? (
                <a
                  href={`https://app.beaconcrm.org/account/${taskCounts.accountId}/records/task/list`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2"
                >
                  <span className={`text-3xl font-semibold group-hover:text-sca-purple-600 transition-colors ${
                    (taskCounts?.unassignedOpenTasks ?? 0) > 0 ? 'text-amber-600' : 'text-gray-900'
                  }`}>
                    {taskCounts?.unassignedOpenTasks ?? 0}
                  </span>
                  <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-sca-purple-500 transition-colors" />
                </a>
              ) : (
                <p className={`text-3xl font-semibold ${
                  (taskCounts?.unassignedOpenTasks ?? 0) > 0 ? 'text-amber-600' : 'text-gray-900'
                }`}>
                  {taskCounts?.unassignedOpenTasks ?? 0}
                </p>
              )}
              {(taskCounts?.unassignedOpenTasks ?? 0) > 0 && (
                <p className="text-xs text-amber-600 mt-1">Needs attention</p>
              )}
            </div>
          </div>
          {tasksLastRetrieved && (
            <p className="text-xs text-gray-400 mt-2">Updated {tasksLastRetrieved}</p>
          )}
          </>
        )}
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent activity placeholder */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <button className="text-sm text-sca-purple-500 hover:text-sca-purple-600 font-medium flex items-center gap-1">
              View all
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="text-center py-8 text-gray-500">
            <CalendarDays className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Activity feed coming soon</p>
            <p className="text-xs mt-1">Recent changes from Beacon will appear here</p>
          </div>
        </div>

        {/* Quick actions */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button className="btn-secondary w-full justify-start">
              <UserCheck className="h-4 w-4 mr-2" />
              Add Volunteer Assignment
            </button>
            <button className="btn-secondary w-full justify-start">
              <Building2 className="h-4 w-4 mr-2" />
              View Organisations
            </button>
            <button className="btn-secondary w-full justify-start">
              <TrendingUp className="h-4 w-4 mr-2" />
              View Reports
            </button>
          </div>
        </div>
      </div>

      {/* Pending card transactions + Email campaigns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pending Card Transactions — hidden if user has no finance access */}
        {moduleAccess.finance !== 'none' && <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-sca-purple-500" />
              Pending Card Transactions
            </h3>
            <Link
              to="/finance"
              className="text-sm text-sca-purple-500 hover:text-sca-purple-600 font-medium flex items-center gap-1"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {cardAttributionsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : !cardAttributions || cardAttributions.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <CreditCard className="h-10 w-10 mx-auto mb-2 text-gray-200" />
              <p className="text-sm">No pending card transactions</p>
              <p className="text-xs mt-1">
                {cardAttributions !== undefined
                  ? 'All clear — or no watched account configured'
                  : 'Configure a watched account in System Settings'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {cardAttributions.map((attr) => {
                const unexplainedCount = attr.transactions.filter(tx => tx.status === 'unexplained').length;
                const unapprovedCount  = attr.unapprovedCount ?? attr.transactions.filter(tx => tx.status === 'unapproved').length;
                const totalUnapproved  = attr.transactions
                  .filter(tx => tx.status === 'unapproved')
                  .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
                return (
                  <div
                    key={attr.memberEmail}
                    className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-full bg-sca-purple-100 text-sca-purple-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {attr.initials}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 mb-1">{attr.memberName}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {unexplainedCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              <CircleDashed className="h-3 w-3" />
                              {unexplainedCount} unexplained · £{attr.totalUnexplained.toFixed(2)}
                            </span>
                          )}
                          {unapprovedCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              <Clock className="h-3 w-3" />
                              {unapprovedCount} to approve · £{totalUnapproved.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-gray-400 pt-1">
                These team members need to explain or approve their card transactions in FreeAgent
                {financeLastRetrieved && ` · Updated ${financeLastRetrieved}`}
              </p>
            </div>
          )}
        </div>}

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Campaigns</h3>
          <p className="text-gray-500 text-sm">
            MailChimp integration coming soon. This widget will display recent campaign
            performance and subscriber statistics.
          </p>
        </div>
      </div>
    </div>
  );
}
